// @flow strict
import React from 'react';
import { useTheme } from '../../hooks';
import styles from './ThemeToggle.module.scss';

type Props = {
  // 'switch' renders a labelled track instead of the icon button. The icon
  // button sits among the social links in some layouts, where it reads as one
  // of them rather than as a control.
  variant?: 'icon' | 'switch'
};

const MOON = 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z';

const Sun = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const ThemeToggle = ({ variant = 'icon' }: Props) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  if (variant === 'switch') {
    return (
      <button
        className={styles['themeSwitch']}
        onClick={toggleTheme}
        // role/aria-checked rather than a label, so it is announced as the
        // on/off control it is.
        role="switch"
        aria-checked={isDark}
        aria-label="Dark mode"
        title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
        type="button"
      >
        <span className={styles['themeSwitch__track']}>
          {/* The knob carries the icon for the mode you are in, so the control
              says which state it is in without needing a written label. */}
          <span className={styles['themeSwitch__knob']}>
            <svg viewBox="0 0 24 24" width="11" height="11" aria-hidden="true" focusable="false">
              {isDark
                ? <path d={MOON} fill="currentColor" />
                : (
                  <g fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                    <circle cx="12" cy="12" r="4.4" />
                    <path d="M12 1.6v2.2M12 20.2v2.2M3.9 3.9l1.6 1.6M18.5 18.5l1.6 1.6M1.6 12h2.2M20.2 12h2.2M3.9 20.1l1.6-1.6M18.5 5.5l1.6-1.6" />
                  </g>
                )}
            </svg>
          </span>
        </span>
      </button>
    );
  }

  return (
    <button
      className={styles['themeToggle']}
      onClick={toggleTheme}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      type="button"
    >
      {isDark ? <Sun /> : (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
          <path d={MOON} />
        </svg>
      )}
    </button>
  );
};

export default ThemeToggle;
